import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Platform } from '@ionic/angular';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private renderer: Renderer2;
  private isDarkMode: boolean = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory: RendererFactory2,
    private platform: Platform
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.initializeTheme();
  }

  private initializeTheme(): void {
    const savedMode: string | null = localStorage.getItem('color-mode');
    this.isDarkMode = savedMode === 'dark';
    this.applyTheme(this.isDarkMode);
  }

  public toggleTheme(): boolean {
    this.isDarkMode = !this.isDarkMode;
    this.applyTheme(this.isDarkMode);
    return this.isDarkMode;
  }

  public setTheme(isDark: boolean): void {
    this.isDarkMode = isDark;
    this.applyTheme(isDark);
  }

  public getIsDarkMode(): boolean {
    return this.isDarkMode;
  }

  private applyTheme(isDark: boolean): void {
    localStorage.setItem('color-mode', isDark ? 'dark' : 'light');

    if (isDark) {
      this.document.body.classList.add('dark');
    } else {
      this.document.body.classList.remove('dark');
    }

    this.updateStatusBarColors(isDark);
  }

  private updateStatusBarColors(isDark: boolean): void {
    const themeColor: string = isDark ? '#121212' : '#ffffff';
    const appleStatusBarStyle: string = isDark ? 'black-translucent' : 'default';

    this.updateMetaTag('theme-color', themeColor);
    this.updateMetaTag('apple-mobile-web-app-status-bar-style', appleStatusBarStyle);
    this.updateMetaTag('msapplication-navbutton-color', themeColor);
    this.updateManifestLink(isDark);

    if (this.platform.is('ios') || this.platform.is('iphone') || this.platform.is('ipad')) {
      this.forceIOSStatusBarUpdate();
    }

    this.updateColorScheme(isDark);
  }

  private updateMetaTag(name: string, content: string): void {
    let metaTag: Element | null = this.document.querySelector(`meta[name="${name}"]`);
    
    if (!metaTag) {
      metaTag = this.document.createElement('meta');
      metaTag.setAttribute('name', name);
      this.document.head.appendChild(metaTag);
    }
    
    this.renderer.setAttribute(metaTag, 'content', content);
    
    if (name === 'apple-mobile-web-app-status-bar-style' && metaTag.parentNode) {
      const parent: ParentNode = metaTag.parentNode;
      const newMetaTag: Node = metaTag.cloneNode(true);
      parent.removeChild(metaTag);
      parent.appendChild(newMetaTag);
    }
  }

  private updateManifestLink(isDark: boolean): void {
    const manifestLink: Element | null = this.document.querySelector('link[rel="manifest"]');
    
    if (manifestLink) {
      const manifestPath: string = isDark ? 'manifest.dark.json' : 'manifest.json';
      this.renderer.setAttribute(manifestLink, 'href', manifestPath);
      
      // WHY: a manifest <link> found in the document always has a parent node.
      const parent: ParentNode = manifestLink.parentNode!;
      const newManifestLink: Element = manifestLink.cloneNode(true) as Element;
      parent.removeChild(manifestLink);
      parent.appendChild(newManifestLink);
    }
  }

  private updateColorScheme(isDark: boolean): void {
    const colorSchemeTag: Element | null = this.document.querySelector('meta[name="color-scheme"]');
    if (colorSchemeTag) {
      this.renderer.setAttribute(colorSchemeTag, 'content', isDark ? 'dark' : 'light');
    }
    this.document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
  }

  private forceIOSStatusBarUpdate(): void {
    const viewportMeta: Element | null = this.document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      const originalContent: string = viewportMeta.getAttribute('content')!;
      this.renderer.setAttribute(viewportMeta, 'content', originalContent + ' ');
      setTimeout(() => {
        this.renderer.setAttribute(viewportMeta, 'content', originalContent);
      }, 10);
    }

    window.scrollTo(0, 1);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  }

  public refreshTheme(): void {
    this.applyTheme(this.isDarkMode);
  }
}
